import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { programApi } from "@bw/shared/api";
import {
  useProgramPreviewIdentity,
  useProgramPreviewQuery,
} from "@bw/shared/hooks";
import type {
  BoundedPublicProgramDetailResponse,
  PublicProgramSessionPreviewPage,
  PublicProgramTrackOptionsPage,
  UnifiedProgramResponse,
} from "@bw/shared/types/generated";
import {
  initialPreviewKey,
  publicPreviewScope,
  publicPreviewWindowKey,
  type CapturedProgramWindow,
} from "./previewKeys";

/**
 * A single program by id (GET /programs/{id}). Server-scoped: the backend falls
 * back to the public facility so a player can read any publicly-visible
 * facility's published program. The page renders its real fields verbatim.
 */
export function useProgram(
  programId: string | undefined,
  facilityId: string | undefined,
  authenticated = false,
) {
  const identity = useProgramPreviewIdentity();
  const client = useQueryClient();
  const query = useProgramPreviewQuery<
    | Omit<
        BoundedPublicProgramDetailResponse,
        "session_preview" | "track_options"
      >
    | UnifiedProgramResponse
  >({
    scope: [
      identity,
      "classes",
      programId ?? "",
      facilityId ?? "",
      authenticated ? "authenticated" : "public",
      "metadata",
    ],
    enabled: Boolean(programId && (authenticated || facilityId)),
    load: async (_signal, isCurrent) => {
      if (authenticated) return programApi.getProgram(programId as string);
      const body = await programApi.getPublicProgram(
        programId as string,
        facilityId as string,
      );
      if (!isCurrent())
        throw new DOMException("Class request was superseded", "AbortError");
      const window = {
        window_start: body.session_preview.window_start,
        window_end: body.session_preview.window_end,
      };
      client.setQueryData(
        publicPreviewWindowKey(
          identity,
          programId as string,
          facilityId as string,
        ),
        window,
      );
      client.setQueryData(
        initialPreviewKey(
          publicPreviewScope(
            identity,
            programId as string,
            facilityId as string,
            "sessions",
            window,
          ),
        ),
        body.session_preview,
      );
      client.setQueryData(
        initialPreviewKey(
          publicPreviewScope(
            identity,
            programId as string,
            facilityId as string,
            "options",
          ),
        ),
        body.track_options,
      );
      const {
        session_preview: _session,
        track_options: _options,
        ...metadata
      } = body;
      return metadata;
    },
  });
  useEffect(
    () => () => {
      const prefix = ["program-preview", identity, "classes", programId ?? ""];
      void client.cancelQueries({ queryKey: prefix });
      client.removeQueries({ queryKey: prefix });
      client.removeQueries({
        queryKey: publicPreviewWindowKey(
          identity,
          programId ?? "",
          facilityId ?? "",
        ),
        exact: true,
      });
    },
    [client, identity, programId, facilityId, authenticated],
  );
  const window = client.getQueryData<CapturedProgramWindow>(
    publicPreviewWindowKey(identity, programId ?? "", facilityId ?? ""),
  );
  return {
    ...query,
    identity,
    window,
    publicSessions: window
      ? client.getQueryData<PublicProgramSessionPreviewPage>(
          initialPreviewKey(
            publicPreviewScope(
              identity,
              programId ?? "",
              facilityId ?? "",
              "sessions",
              window,
            ),
          ),
        )
      : undefined,
    publicOptions: client.getQueryData<PublicProgramTrackOptionsPage>(
      initialPreviewKey(
        publicPreviewScope(
          identity,
          programId ?? "",
          facilityId ?? "",
          "options",
        ),
      ),
    ),
  };
}
